import { type AxiosResponse } from 'axios';
import { addBusinessDays, endOfDay, formatRFC3339, startOfDay, subBusinessDays } from 'date-fns';
import FileSaver from 'file-saver';
import { call, delay, fork, put, race, take, takeLatest } from 'redux-saga/effects';

import type Manifest from '~/types/Manifests';

import { ActionKeys } from '~/actions/constants';
import * as manifestsActions from '~/actions/manifests.actions';
import * as manifestsService from '~/services/manifests.service';
import { type ManifestGroupsResponse, type ManifestsGetResponse } from '~/types/Manifests';
import { ManifestFromJSON, ManifestGroupsFromJSON, ManifestStatus } from '~/types/Manifests';
import { newRelic } from '~/utils/newRelic/newRelic';

export function* getManifestsGroups(
  action: ReturnType<typeof manifestsActions.fetchManifestsGroups>,
) {
  try {
    const { carrierObjectId, dateRangeEnd, dateRangeStart } = action.payload;
    const {
      data: { results },
    }: ManifestGroupsResponse = yield call(
      manifestsService.getManifestGroups,
      dateRangeStart,
      dateRangeEnd,
      carrierObjectId,
    );
    yield put(manifestsActions.fetchManifestsGroupsSuccess(ManifestGroupsFromJSON(results)));
  } catch (error) {
    yield put(manifestsActions.fetchManifestsGroupsError(error as Error));
    yield call(newRelic.noticeError, 'Failed to fetch manifest groups', { error });
  }
}

export function* getRecentManifests() {
  try {
    const today = startOfDay(Date.now());
    // 5 business days from now
    const dateRangeStart = formatRFC3339(subBusinessDays(today, 5));

    // Manifests can be created 5 days in advance
    const dateRangeEnd = formatRFC3339(endOfDay(addBusinessDays(today, 5)));

    const {
      data: { results },
    }: ManifestsGetResponse = yield call(
      manifestsService.getManifestsByDateRange,
      dateRangeStart,
      dateRangeEnd,
    );

    const manifests = results.map(ManifestFromJSON);

    yield put(manifestsActions.fetchManifestsSuccess(manifests));

    const manifestsToPoll = manifests
      .filter((manifest) => manifest.objectStatus === ManifestStatus.QUEUED)
      .map((manifest) => manifest.objectId);
    if (manifestsToPoll.length) yield put(manifestsActions.startManifestsPolling(manifestsToPoll));
  } catch (error) {
    yield put(manifestsActions.fetchManifestsError(error as Error));
    yield call(newRelic.noticeError, 'Failed to fetch recent manifests', { error });
  }
}

function* downloadManifestZip(
  action: ReturnType<typeof manifestsActions.downloadManifestDocumentZip>,
) {
  try {
    const response: AxiosResponse<ArrayBuffer> = yield call(
      manifestsService.downloadManifestDocumentsZip,
      action.payload,
    );
    const blob = new Blob([response.data], { type: 'application/zip' });
    FileSaver.saveAs(blob, `manifest_${action.payload.toString()}.zip`);
    yield put(manifestsActions.downloadManifestDocumentZipSuccess());
  } catch (error) {
    yield put(manifestsActions.downloadManifestDocumentZipError());
    yield call(newRelic.noticeError, 'Failed to download manifest ZIP', { error });
  }
}

export function* generateManifest(action: ReturnType<typeof manifestsActions.generateManifest>) {
  try {
    const response: AxiosResponse<{ object_id?: string }> = yield call(
      manifestsService.generateManifest,
      action.payload,
    );

    if (response?.data?.object_id)
      yield put(manifestsActions.startManifestsPolling([response.data.object_id]));

    yield put(manifestsActions.generateManifestSuccess());
  } catch (err) {
    let errorMessage = 'Something unexpected happened on our end.';
    if ((err as any)?.response?.data?.detail) errorMessage = (err as any).response.data.detail; // eslint-disable-line @typescript-eslint/no-explicit-any

    yield put(manifestsActions.generateManifestError(new Error(errorMessage)));
  }
}

let worker = null;
let manifestsToPoll: string[] = [];

export function* manifestsPollWorker(manifestsToPoll: string[]) {
  const MANIFEST_POLL_WAIT_TIME = 1000; // 1 second

  while (true) {
    if (manifestsToPoll.length) {
      try {
        const {
          data: { results },
        }: ManifestsGetResponse = yield call(
          manifestsService.getManifestsByObjectIds,
          manifestsToPoll,
        );
        if (results && results.length) {
          const manifests = results.map(ManifestFromJSON);

          const manifestsInTerminalState: Manifest[] = [];
          for (const manifest of manifests) {
            if ([ManifestStatus.ERROR, ManifestStatus.SUCCESS].includes(manifest.objectStatus))
              manifestsInTerminalState.push(manifest);
          }

          if (manifestsInTerminalState.length) {
            yield put(
              manifestsActions.fetchManifestsSuccessTerminalState(manifestsInTerminalState),
            );
          }

          // Stop polling when all manifests have reached terminal state
          if (manifestsInTerminalState.length === manifestsToPoll.length) {
            manifestsToPoll = [];
            yield put(manifestsActions.stopManifestsPolling());
          }
        }
      } catch (err) {
        yield put(manifestsActions.stopManifestsPolling());
      }
    }
    // None to poll
    else {
      yield put(manifestsActions.stopManifestsPolling());
    }

    if (ENV_ID === 'test') break;

    yield delay(MANIFEST_POLL_WAIT_TIME);
  }
}

/**
 * Watches for the MANIFESTS_POLL_START redux action to start the polling process by
 * forking the race effect generator.
 */
export function* manifestsPollWatcher() {
  while (true) {
    const action: ReturnType<typeof manifestsActions.startManifestsPolling> = yield take(
      ActionKeys.MANIFESTS_POLL_START,
    );

    manifestsToPoll = action.payload.manifestsToPoll;

    if (!worker) worker = yield fork(manifestsPollRace);
  }
}

// Calls the poll worker with the manifests to poll for. Cancels on MANIFESTS_POLL_END.
// The timeout is important for the very rare cases where a generate manifest request gets
// stuck in QUEUED state!
function* manifestsPollRace() {
  const MANIFEST_POLL_TIMEOUT = 1 * 25 * 1000; // 25 seconds
  const result = yield race({
    cancel: take(ActionKeys.MANIFESTS_POLL_END),
    task: call(manifestsPollWorker, manifestsToPoll),
    timeout: delay(MANIFEST_POLL_TIMEOUT, true),
  });

  if (result.timeout) {
    manifestsToPoll = [];
    yield put(manifestsActions.stopManifestsPolling());
  }

  worker = null;
}

export default [
  manifestsPollWatcher(),
  takeLatest(ActionKeys.MANIFESTS_GROUPS_FETCH, getManifestsGroups),
  takeLatest(ActionKeys.MANIFESTS_FETCH, getRecentManifests),
  takeLatest(ActionKeys.MANIFESTS_DOWNLOAD_ZIP, downloadManifestZip),
  takeLatest(ActionKeys.MANIFESTS_GENERATE, generateManifest),
];
